<template>
  <router-view />
</template>

<script setup lang="ts">
// Root component. Global providers (Pinia, urql, Keycloak) are installed via
// boot files; routing renders the layout + module registry.
import { onMounted, watch } from 'vue'
import { coreDataSessionReady, useCoreDataStore } from 'src/stores/coreData'
import { useAuthStore } from 'src/stores/auth'

// Lazy prefetch of the five public quasi-static core-data collections
// (ui-conventions.md #94, owner ruling 2026-08-22): kicked off once, after
// first paint, so cold boot never waits on it. requestIdleCallback isn't
// available in every engine (Safari), hence the setTimeout fallback.
//
// Session-aware (ui-report 01a03b1f): the prefetch waits for a session that
// can actually fetch — on the pre-auth funnel or an unlinked account the
// queries only fail into Sentry. The watch fires once, whenever readiness
// arrives (immediately in mock mode, after the `Query.me` sync otherwise).
onMounted(() => {
  const auth = useAuthStore()
  let fired = false
  const stop = watch(
    () => coreDataSessionReady(auth),
    (ready) => {
      if (!ready || fired) return
      fired = true
      const load = () => useCoreDataStore().ensureLoaded()
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(load)
      } else {
        setTimeout(load, 1500)
      }
    },
    { immediate: true },
  )
  if (fired) stop()
})
</script>
